import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plane, Building2, MapPin, Calendar, Star, Clock, ExternalLink, Search, Filter, Users, Wifi, Coffee, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import { useCurrency } from '@/lib/currency-context';
import AirportTransferOffer, { SmartTransferSuggestion } from '@/components/taxi/AirportTransferOffer';
import { PageSeo } from '@/components/seo/PageSeo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const HotelFlightsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency, convertPrice, formatPrice } = useCurrency();
  
  // Get type from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const typeFromUrl = urlParams.get('type'); // 'hotels' or 'flights'
  
  // Determine initial tab and if tabs should be shown
  const showBothTabs = !typeFromUrl; // Show both tabs only if no type specified
  const initialTab = typeFromUrl === 'flights' ? 'flights' : 'hotels';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [hotels, setHotels] = useState([]);
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingData, setBookingData] = useState({
    check_in: '',
    check_out: '',
    guests: 1,
    rooms: 1,
    room_type: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    special_requests: ''
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  
  // Airport Transfer Offer states
  const [showTransferOffer, setShowTransferOffer] = useState(false);
  const [showTransferSuggestion, setShowTransferSuggestion] = useState(false);
  const [lastBookedItem, setLastBookedItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [hotelsRes, flightsRes] = await Promise.all([
        fetch(`${API_URL}/api/hotel-flights/hotels?limit=100`),
        fetch(`${API_URL}/api/hotel-flights/flights?limit=100`)
      ]);

      if (hotelsRes.ok) {
        const data = await hotelsRes.json();
        setHotels(data.hotels || data.items || []);
      }

      if (flightsRes.ok) {
        const data = await flightsRes.json();
        setFlights(data.flights || data.items || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const filteredHotels = hotels.filter(hotel =>
    hotel.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    hotel.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    hotel.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFlights = flights.filter(flight =>
    flight.airline?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    flight.departure_city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    flight.arrival_city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getHotelImage = (hotel) => {
    if (hotel.images && hotel.images.length > 0) {
      return hotel.images[0];
    }
    const defaultImages = [
      'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400',
      'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400',
      'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400',
      'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400'
    ];
    return defaultImages[Math.floor(Math.random() * defaultImages.length)];
  };

  const handleHotelBooking = async () => {
    if (!user) {
      toast.error(t('hotelFlight.loginRequired'));
      setBookingDialogOpen(false);
      navigate('/login?redirect=/hotel-flights');
      return;
    }

    if (!bookingData.check_in || !bookingData.check_out) {
      toast.error(t('hotelFlight.selectDates'));
      return;
    }

    if (!bookingData.customer_name || !bookingData.customer_phone || !bookingData.customer_email) {
      toast.error(t('hotelFlight.fillContactInfo'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/hotel-flights/hotels/${selectedItem.id}/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hotel_id: selectedItem.id,
          check_in_date: bookingData.check_in,
          check_out_date: bookingData.check_out,
          room_type: bookingData.room_type || selectedItem.room_types?.[0]?.name || 'Standart Oda',
          guests: bookingData.guests,
          rooms: bookingData.rooms,
          customer_name: bookingData.customer_name,
          customer_phone: bookingData.customer_phone,
          customer_email: bookingData.customer_email,
          special_requests: bookingData.special_requests
        })
      });

      if (res.ok) {
        toast.success(t('hotelFlight.bookingSuccess'));
        setBookingDialogOpen(false);
        
        // Store booked item for transfer offer
        setLastBookedItem({
          type: 'hotel',
          item: selectedItem,
          bookingData: { ...bookingData }
        });
        
        // Show transfer suggestion after successful booking
        setShowTransferSuggestion(true);
        
        setBookingData({ 
          check_in: '', check_out: '', guests: 1, rooms: 1,
          customer_name: user?.name || '', customer_phone: '', customer_email: user?.email || '',
          room_type: '', special_requests: ''
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(t('common.error'));
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < (rating || 4) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <PageSeo title={t('hotelFlight.heroTitle')} description={t('hotelFlight.heroSubtitle')} segments={['hotel-flights']} />
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <Plane className="w-5 h-5" />
              <span className="text-sm font-medium">{t('hotelFlight.title')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
              {t('hotelFlight.heroTitle')}
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              {t('hotelFlight.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="container mx-auto px-4 -mt-8 mb-8">
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder={t('hotelFlight.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Tabs Content */}
      <section className="container mx-auto px-4 pb-16">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Only show tabs if no specific type is selected from URL */}
          {showBothTabs ? (
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="hotels" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {t('nav.hotels')} ({filteredHotels.length})
              </TabsTrigger>
              <TabsTrigger value="flights" className="flex items-center gap-2">
                <Plane className="w-4 h-4" />
                {t('nav.flights')} ({filteredFlights.length})
              </TabsTrigger>
            </TabsList>
          ) : (
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
                {typeFromUrl === 'flights' ? (
                  <>
                    <Plane className="w-6 h-6 text-primary" />
                    {t('hotelFlight.allFlights')}
                  </>
                ) : (
                  <>
                    <Building2 className="w-6 h-6 text-primary" />
                    {t('hotelFlight.allHotels')}
                  </>
                )}
              </h2>
            </div>
          )}

          {/* Hotels Tab */}
          <TabsContent value="hotels">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4 space-y-3">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredHotels.length === 0 ? (
              <Card className="p-12 text-center">
                <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t('hotelFlight.noHotelsFound')}</h3>
                <p className="text-muted-foreground">{t('hotelFlight.tryDifferentSearch')}</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredHotels.map((hotel) => (
                  <Card key={hotel.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={getHotelImage(hotel)}
                        alt={hotel.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {hotel.stars && (
                        <div className="absolute top-3 left-3 flex gap-0.5 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                          {renderStars(hotel.stars)}
                        </div>
                      )}
                      {hotel.integration_type && (
                        <Badge className="absolute top-3 right-3 bg-blue-600">
                          {hotel.integration_type}
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-bold text-lg mb-1">{hotel.name}</h3>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4" />
                        <span>{hotel.location || hotel.city}</span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {hotel.description}
                      </p>

                      {hotel.amenities && hotel.amenities.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {hotel.amenities.slice(0, 3).map((amenity, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {amenity}
                            </Badge>
                          ))}
                          {hotel.amenities.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{hotel.amenities.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      {hotel.price_per_night && (
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-primary">
                            {formatPrice(convertPrice(hotel.price_per_night, hotel.currency))}
                          </span>
                          <span className="text-sm text-muted-foreground">/{t('hotelFlight.perNight')}</span>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="p-4 pt-0 flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="flex-1">
                            {t('hotelFlight.viewDetails')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              {hotel.name}
                              {hotel.stars && (
                                <div className="flex gap-0.5">
                                  {renderStars(hotel.stars)}
                                </div>
                              )}
                            </DialogTitle>
                            <DialogDescription className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {hotel.location || hotel.city}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid md:grid-cols-2 gap-6">
                            <img
                              src={getHotelImage(hotel)}
                              alt={hotel.name}
                              className="w-full h-64 object-cover rounded-lg"
                            />
                            <div className="space-y-4">
                              <p className="text-muted-foreground">{hotel.description}</p>
                              
                              {hotel.amenities && hotel.amenities.length > 0 && (
                                <div className="space-y-2">
                                  <h4 className="font-semibold">{t('hotelFlight.amenities')}</h4>
                                  <div className="flex flex-wrap gap-1">
                                    {hotel.amenities.map((amenity, i) => (
                                      <Badge key={i} variant="outline">{amenity}</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {hotel.price_per_night && (
                                <div className="p-4 bg-muted rounded-lg">
                                  <div className="flex justify-between items-center">
                                    <span className="font-medium">{t('hotelFlight.pricePerNight')}</span>
                                    <span className="text-2xl font-bold text-primary">
                                      {formatPrice(convertPrice(hotel.price_per_night, hotel.currency))}
                                    </span>
                                  </div>
                                </div>
                              )}

                              {hotel.external_link && (
                                <Button asChild variant="outline" className="w-full">
                                  <a href={hotel.external_link} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    {t('hotelFlight.viewOnPartner')}
                                  </a>
                                </Button>
                              )}
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button 
                        className="flex-1"
                        onClick={() => {
                          setSelectedItem(hotel);
                          setBookingDialogOpen(true);
                        }}
                      >
                        {t('hotelFlight.bookNow')}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Flights Tab */}
          <TabsContent value="flights">
            {loading ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <Card key={i} className="p-6">
                    <div className="flex items-center gap-6">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-6 w-1/2" />
                        <Skeleton className="h-4 w-1/3" />
                      </div>
                      <Skeleton className="h-10 w-24" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredFlights.length === 0 ? (
              <Card className="p-12 text-center">
                <Plane className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t('hotelFlight.noFlightsFound')}</h3>
                <p className="text-muted-foreground">{t('hotelFlight.tryDifferentSearch')}</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredFlights.map((flight) => (
                  <Card key={flight.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                        {/* Airline Info */}
                        <div className="flex items-center gap-3 min-w-[150px]">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <Plane className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold">{flight.airline}</p>
                            <p className="text-sm text-muted-foreground">{flight.flight_number}</p>
                          </div>
                        </div>

                        {/* Flight Route */}
                        <div className="flex-1 flex items-center justify-between md:justify-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold">{flight.departure_time || '09:00'}</p>
                            <p className="text-sm text-muted-foreground">{flight.departure_city}</p>
                          </div>
                          <div className="flex-1 max-w-[200px] flex items-center gap-2">
                            <div className="h-px flex-1 bg-border" />
                            <Plane className="w-4 h-4 text-muted-foreground" />
                            <div className="h-px flex-1 bg-border" />
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold">{flight.arrival_time || '12:00'}</p>
                            <p className="text-sm text-muted-foreground">{flight.arrival_city}</p>
                          </div>
                        </div>

                        {/* Price & Book */}
                        <div className="flex items-center gap-4 md:min-w-[200px] justify-between md:justify-end">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary">
                              {formatPrice(convertPrice(flight.price || 299, flight.currency))}
                            </p>
                            <p className="text-sm text-muted-foreground">{t('hotelFlight.perPerson')}</p>
                          </div>
                          {flight.external_link ? (
                            <Button asChild>
                              <a href={flight.external_link} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-4 h-4 mr-2" />
                                {t('hotelFlight.book')}
                              </a>
                            </Button>
                          ) : (
                            <Button>
                              {t('hotelFlight.book')}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Flight Details */}
                      {(flight.duration || flight.stops !== undefined) && (
                        <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {flight.duration && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{flight.duration}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <span>{flight.stops === 0 ? t('hotelFlight.direct') : `${flight.stops} ${t('hotelFlight.stops')}`}</span>
                          </div>
                          {flight.class && (
                            <Badge variant="outline">{flight.class}</Badge>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>

      {/* Hotel Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('hotelFlight.bookHotel')}</DialogTitle>
            <DialogDescription>
              {selectedItem?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('hotelFlight.checkIn')}</Label>
                <Input
                  type="date"
                  value={bookingData.check_in}
                  onChange={(e) => setBookingData({...bookingData, check_in: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('hotelFlight.checkOut')}</Label>
                <Input
                  type="date"
                  value={bookingData.check_out}
                  onChange={(e) => setBookingData({...bookingData, check_out: e.target.value})}
                  min={bookingData.check_in || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            
            {/* Guests & Rooms */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('hotelFlight.guests')}</Label>
                <Select 
                  value={bookingData.guests.toString()} 
                  onValueChange={(v) => setBookingData({...bookingData, guests: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {t('hotelFlight.guest')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('hotelFlight.rooms')}</Label>
                <Select 
                  value={bookingData.rooms.toString()} 
                  onValueChange={(v) => setBookingData({...bookingData, rooms: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {t('hotelFlight.room')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Room Type */}
            {selectedItem?.room_types?.length > 0 && (
              <div className="space-y-2">
                <Label>{t('hotelFlight.roomType')}</Label>
                <Select 
                  value={bookingData.room_type || selectedItem.room_types[0]?.name} 
                  onValueChange={(v) => setBookingData({...bookingData, room_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedItem.room_types.map(room => (
                      <SelectItem key={room.name} value={room.name}>
                        {room.name} - {formatPrice(convertPrice(room.price || 0))}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Customer Info */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">{t('hotelFlight.contactInfo')}</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>{t('hotelFlight.customerName')} *</Label>
                  <Input
                    value={bookingData.customer_name}
                    onChange={(e) => setBookingData({...bookingData, customer_name: e.target.value})}
                    placeholder={t('hotelFlight.enterName')}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('hotelFlight.customerPhone')} *</Label>
                    <Input
                      type="tel"
                      value={bookingData.customer_phone}
                      onChange={(e) => setBookingData({...bookingData, customer_phone: e.target.value})}
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('hotelFlight.customerEmail')} *</Label>
                    <Input
                      type="email"
                      value={bookingData.customer_email}
                      onChange={(e) => setBookingData({...bookingData, customer_email: e.target.value})}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('hotelFlight.specialRequests')}</Label>
                  <Input
                    value={bookingData.special_requests}
                    onChange={(e) => setBookingData({...bookingData, special_requests: e.target.value})}
                    placeholder={t('hotelFlight.specialRequestsPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Price Summary */}
            {selectedItem && selectedItem.price_per_night && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{t('hotelFlight.pricePerNight')}</span>
                  <span className="text-xl font-bold text-primary">
                    {formatPrice(convertPrice(selectedItem.price_per_night, selectedItem.currency))}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleHotelBooking} disabled={bookingLoading}>
              {bookingLoading ? t('common.loading') : t('hotelFlight.confirmBooking')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Airport Transfer Offer - Shows after successful booking */}
      {showTransferOffer && lastBookedItem && (
        <AirportTransferOffer
          isOpen={showTransferOffer}
          onClose={() => setShowTransferOffer(false)}
          flightInfo={lastBookedItem.type === 'flight' ? {
            arrival_airport: lastBookedItem.item?.arrival_city,
            arrival_time: lastBookedItem.item?.arrival_time,
            flight_number: lastBookedItem.item?.flight_number
          } : null}
          hotelInfo={lastBookedItem.type === 'hotel' ? {
            name: lastBookedItem.item?.name,
            address: lastBookedItem.item?.address,
            city: lastBookedItem.item?.city
          } : null}
          customerInfo={{
            name: lastBookedItem.bookingData?.customer_name,
            phone: lastBookedItem.bookingData?.customer_phone,
            email: lastBookedItem.bookingData?.customer_email
          }}
        />
      )}

      {/* Smart Transfer Suggestion - Shows as floating popup */}
      {showTransferSuggestion && lastBookedItem && (
        <SmartTransferSuggestion
          bookingType={lastBookedItem.type}
          bookingDetails={lastBookedItem.item}
          onAccept={() => {
            setShowTransferSuggestion(false);
            setShowTransferOffer(true);
          }}
          onDismiss={() => setShowTransferSuggestion(false)}
        />
      )}
    </div>
  );
};

export default HotelFlightsPage;
